import React, { useEffect, useRef, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../src/app/authConfig';
import { getUserProfile } from './app/Services/graphService';

interface AuthWrapperProps {
    children: React.ReactNode;
}

const AUTHORITY = 'https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6';

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const [isReady, setIsReady] = useState(false);
    // Prevent the effect from running auth init more than once per session
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;

        if (!isAuthenticated) {
            const intendedUrl = window.location.pathname + window.location.search;
            if (intendedUrl !== '/' && intendedUrl !== '/awards/') {
                sessionStorage.setItem('postLoginRedirect', intendedUrl);
            }
            instance.loginRedirect({ scopes: ["User.Read", "User.Read.All"] });
            return;
        }

        // Only run init once per mount — prevents re-firing when other state changes
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const initAuth = async () => {
            try {
                const currentAccounts = instance.getAllAccounts();
                if (currentAccounts.length === 0) {
                    instance.loginRedirect(loginRequest);
                    return;
                }

                // Fetch and cache the user profile. getUserProfile acquires the token
                // silently itself, so we don't do a separate acquireTokenSilent here —
                // that was a redundant second round-trip on every load.
                if (accounts && accounts.length > 0) {
                    await getUserProfile(instance, AUTHORITY, true);
                }

                setIsReady(true);

                const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
                if (postLoginRedirect) {
                    sessionStorage.removeItem('postLoginRedirect');
                    navigate(postLoginRedirect);
                }
            } catch (error) {
                console.error("Auth init failed, redirecting to SSO login:", error);
                instance.loginRedirect(loginRequest);
            }
        };

        initAuth();
    }, [instance, isAuthenticated, inProgress, accounts, navigate]);

    // Don't render children until authentication is complete and ready
    if (inProgress !== InteractionStatus.None) {
        return <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
        }}>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
        }}>Redirecting to login...</div>;
    }

    if (!isReady) {
        return <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
        }}>Authenticating...</div>;
    }

    return <>{children}</>;
};

export default AuthWrapper;